import { FC } from 'react'
import useTranslation from 'next-translate/useTranslation'
import dayjs from 'dayjs'
import { useRouter } from 'next/router'
import { FlexboxGrid, Stack } from 'rsuite'

import TranslateWrapper from './ugcTranslate/TranslateWrapper'
import TranslatableText from './ugcTranslate/TranslatableText'
import TranslateButton from './ugcTranslate/TranslateButton'
import { ReviewMinimized } from '../../../utils/types/Review'
import { getCountryAndLocaleStrings } from '../../../utils/locales'
import { getDateFormatReviewsSection } from '../../../lib/time'
import ImageGrid from './ImageGrid'
import renderQuestionsRecursively from './RenderQuestionsRecursively'
import VerifiedIcon from '../../Icons/VerifiedIcon'
import ReviewStars from './ReviewStars'
import { getImageURLs } from '../../../utils/reviewUtils'

import styles from '../../../styles/SingleProductReviewsSection.module.less'

interface ReviewCardProps {
  review: ReviewMinimized
}

const ReviewCard: FC<ReviewCardProps> = ({ review }) => {
  const { t } = useTranslation('products')
  const { locale: countryAndLocale } = useRouter()

  const { locale } = getCountryAndLocaleStrings(countryAndLocale)

  const reviewLang = review?.['review.data']['review.data.locale'] ?? ''
  const reviewDateString = review?.['review.submitdate']
    ? dayjs(review['review.submitdate'])
      .locale(locale)
      .format(`${getDateFormatReviewsSection(locale)} YYYY`)
    : ''

  const reviewComment = review?.['review.comment'] ?? ''
  const conditionalQuestions = review?.['review.data']['review.data.conditionalquestions']
  const imageURLs = review ? getImageURLs(review) : []
  const attachmentCount = imageURLs.length
  const isBuyerVerified = (review['review.data']['review.data.source'] ?? 1) === 1 // ?? 1 is for
  // old reviews (all verified) that don't have the source field yet
  const isBuyerVerifiedText = isBuyerVerified
    ? t('Verified buyer')
    : t('Online buyer')
  const sku = review['review.data']['review.data.prodcode']

  return (
    <TranslateWrapper sourceLang={reviewLang}>
      <Stack
        direction="column"
        spacing={15}
        alignItems="stretch"
      >
        <Stack
          direction="column"
          spacing={5}
          alignItems="stretch"
        >
          <Stack
            direction="row"
            wrap
            className={styles['stack-custom-spacing']}
          >
            <ReviewStars
              average={review['review.rating']}
              minimize
            />
            <strong>{review['review.name'] || t('Anonymous buyer')}</strong>
            <Stack
              direction="row"
              spacing={4}
              alignItems="stretch"
            >
              <FlexboxGrid justify="center">
                <VerifiedIcon />
              </FlexboxGrid>
              <p className={`h6 ${styles['buyer-tag-anonymous']}`}>
                {isBuyerVerifiedText}
              </p>
            </Stack>
          </Stack>
          <p className={styles.italic}>
            {t('Reviewed {{ sku }} on {{ date }}', { sku, date: reviewDateString })}
          </p>
        </Stack>
        { !!reviewComment && <TranslatableText originalText={reviewComment} /> }
        { !!conditionalQuestions && renderQuestionsRecursively(conditionalQuestions) }
        { !!attachmentCount && <ImageGrid imageURLs={getImageURLs(review)} /> }
      </Stack>
      <TranslateButton />
    </TranslateWrapper>
  )
}

export default ReviewCard
